import { Stack, Typography } from '@mui/material';
import Chip from '/@/components/bayer/chip';

export type ChipOption = { value: number | null; label: string };

/**
 * Single-choice row of chips — the design's replacement for the select inputs the
 * legacy crop-configuration module uses for soil moisture / climate pattern.
 */
function ChipSelect({
    title,
    options,
    value,
    onChange,
    disabled,
    helperText,
    action,
    dataTestId,
    stretch,
}: {
    title: string;
    options: ChipOption[];
    value: number | null | undefined;
    onChange: (value: number | null) => void;
    disabled?: boolean;
    helperText?: string;
    action?: React.ReactNode;
    dataTestId?: string;
    /** Spread the chips evenly across the full width — the mobile layout. */
    stretch?: boolean;
}) {
    return (
        <Stack spacing={2} flexGrow={1} width={stretch ? '100%' : undefined}>
            <Typography variant='subtitle1' fontWeight={700} color='primary'>
                {title}
            </Typography>
            <Stack spacing={1} alignItems={stretch ? 'stretch' : 'flex-start'}>
                <Stack
                    direction='row'
                    spacing={1}
                    flexWrap={stretch ? 'nowrap' : 'wrap'}
                    useFlexGap
                    data-testid={dataTestId}
                >
                    {options.map((option) => (
                        <Chip
                            key={String(option.value)}
                            label={option.label}
                            selected={option.value === value}
                            disabled={disabled}
                            onClick={disabled ? undefined : () => onChange(option.value)}
                            sx={stretch ? { flex: 1, minWidth: 0 } : undefined}
                        />
                    ))}
                </Stack>
                {helperText && (
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {helperText}
                    </Typography>
                )}
                {action}
            </Stack>
        </Stack>
    );
}

export default ChipSelect;
